import React from 'react';
import classNames from 'classnames';
import { Field } from 'react-final-form';

import css from './FieldToggle.module.css';

/**
 * A binary on/off switch for Final Form.
 *
 * Sibling of FieldCheckbox rather than a replacement for it: a checkbox says
 * "this will be true once you save", a switch says "this is the state now".
 * Use it where the label describes a state (Aktiv), not a choice.
 *
 * Renders a real <input type="checkbox"> so the browser gives us the
 * keyboard behaviour, focus handling and form semantics for free; the visible
 * track and knob are painted on top and the input itself is only visually
 * hidden, never `display: none`.
 *
 * @component
 * @param {Object} props
 * @param {string} props.id - input id, also the label's `for`
 * @param {string} props.name - Final Form field name
 * @param {ReactNode} [props.label] - the state this switch governs
 * @param {ReactNode} [props.description] - one line under the label
 * @param {boolean} [props.disabled]
 * @param {string} [props.className]
 * @param {string} [props.rootClassName]
 * @returns {JSX.Element}
 */
const FieldToggle = props => {
  const { rootClassName, className, id, name, label, description, disabled = false } = props;
  const classes = classNames(rootClassName || css.root, className);

  return (
    <Field name={name} type="checkbox">
      {({ input }) => (
        <div className={classes}>
          <div className={css.text}>
            {label ? (
              <label className={css.label} htmlFor={id}>
                {label}
              </label>
            ) : null}
            {description ? <p className={css.description}>{description}</p> : null}
          </div>

          <span className={css.switch}>
            <input
              {...input}
              id={id}
              className={css.input}
              type="checkbox"
              disabled={disabled}
            />
            {/* Painted state. aria-hidden because the input above already
                carries the role, the checked state and the label. */}
            <span className={css.track} aria-hidden="true">
              <span className={css.knob} />
            </span>
          </span>
        </div>
      )}
    </Field>
  );
};

export default FieldToggle;
